import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSplash } from '../../../contexts/SplashContext';
import TranslatableInput from '../../Common/TranslatableInput';
import TranslatableTextarea from '../../Common/TranslatableTextarea';
import LanguageSwitch from '../../Common/LanguageSwitch';
import CommonInput from '../../Common/CommonInput';
import CommonGroupInput from '../../Common/CommonGroupInput';
import CommonTextarea from '../../Common/CommonTextarea';
import CommonSelect from '../../Common/CommonSelect';
import CommonAsyncSelect from '../../Common/CommonAsyncSelect';
import CommonDate from '../../Common/CommonDate';
import CommonImageRadio from '../../Common/CommonImageRadio';
import CommonIconRadio from '../../Common/CommonIconRadio';
import CommonCheckbox from '../../Common/CommonCheckbox';
import ItemInput from '../../Common/ItemInput';

import { fetchSettings } from '../../../store/modules/joborder/joborderActions';
import useFormSettings from '../../../hooks/useFormSettings';

import { setCustomerSettings } from '../../../store/modules/customer/customerSlice';
import HandleQuickCreate from '../../Common/HandleQuickCreate';
import CustomerForm from '../Customers/CustomerForm';
import { createCustomer } from '../../../store/modules/customer/customerActions';

import {Tab, Nav} from 'react-bootstrap';
import { 
    calculateItemValues,
    calculateGrossTotal, 
    calculateOverallDiscount,
    distributeHeaderDiscount,
    calculateTotals
} from './JobOrderCalculation';

import customerService from '../../../services/customerService';

import useModal from '../../../hooks/useModal';
import DataSelectionModal from '../../Common/CommonModal/DataSelectionModal';
import FinishedItems from '../../../pages/Items/FinishedItems'; // Adjust the path as needed
import Customers from '../../../pages/Customers/Customers';

import { useHasPermission } from '../../../utils/permissionUtils';

const JobOrderForm = ({ initialData, onSave, isLoading, onClose, customActionsDispatch = {} }) => {
    
    const hasCreatePermission = useHasPermission(['create job orders']);
    const hasUpdatePermission = useHasPermission(['update job orders']);

    const { settings, isLoadingSettings, isSettingsInitialized } = useFormSettings('joborder', fetchSettings);
    const { countries, locations, branches, customer_locations, selected } = settings || {};

    const splashData = useSplash();
    const languages = splashData?.languages || [];
    const defaultLanguage = languages.find(lang => lang.default)?.code || languages[0]?.code || 'en';
    const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);

    const [typingTimer, setTypingTimer]         = useState(null);
    const doneTypingInterval = 800;

    const [formData, setFormData] = useState({
        id: '',
        is_locked: false,
        order_number: '',
        order_date: '',
        customer: null,
        customer_name: {},
        phone_number: '',
        vat_number: '',
        is_domestic: true,
        address1: {},
        address2: '',
        country: null,
        location: null,
        customer_location: null,
        trial_date: '',
        due_date: '',
        order_detail_info:'',
        vat_group: null,
        branch: null,
        items: [],
        trouser: {
            id: '',
            waist_measurement: 0,
            hip_measurement: 0,
            waist_line_to_heel: 0,
            thigh_measurement: 0,
            knee_measurement: 0,
            bottom_measurement: 0,
            waist_to_crotch: 0,
            back_condition: ''
        },
        jacket: {
            id: '',
            shoulder_seam_to_first_bone: 0,
            around_chest: 0,
            around_waist: 0,
            around_hip: 0,
            tip_to_tip_shoulder: 0,
            center_back_sleeve_seam: 0,
            shoulder_tip_to_below_wrist: 0,
            color_seam_natural_waist: 0,
            shoulder_seam_mid_rise: 0,
            sleeve_length: 0,
            neck_around: 0,

            shoulder_type: '',
            chest_type: ''
        },
        gross_total: '',
        discount_percentage: '',
        discount_amount: '',
        sub_total: '',
        vat_amount: '',

        net_total: '',
        is_advance_job: false,
        advance_payment: '',
        payment: '',
        balance: '',
        payment_type: '',
        payment_date:  new Date().toISOString().split('T')[0],
        payment_amount: '',
        payment_reference: '',
        payments: []
    });
    
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('trouser');

    // Add effect to update active tab when errors change
    useEffect(() => {
        if (Object.keys(errors).some(key => key.startsWith('trouser.'))) {
            setActiveTab('trouser');
        } else if (Object.keys(errors).some(key => key.startsWith('jacket.'))) {
            setActiveTab('jacket');
        } else if (Object.keys(errors).some(key => key.startsWith('items.'))) {
            setActiveTab('items');
        }
    }, [errors]);

    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                id: initialData.id || '',
                is_locked: initialData.is_locked || false,
                order_number: initialData.order_number || '',
                order_date: initialData.order_date || '',
                customer: initialData.customer || null,
                customer_name: initialData.customer_name || {},
                phone_number: initialData.phone_number || '',
                vat_number: initialData.vat_number || '',
                is_domestic: initialData.is_domestic || true,
                address1: initialData.address1 || {},
                address2: initialData.address2 || '',
                country: initialData.country || null,
                location: initialData.location || null,
                customer_location: initialData.customer_location || null,
                vat_group: initialData.vat_group || null,
                branch: initialData.branch || null,
                items: initialData.items || [],
                trial_date: initialData.trial_date || '',
                due_date: initialData.due_date || '',
                order_detail_info: initialData.order_detail_info || '',

                discount_percentage: initialData.discount_percentage || '',
                discount_amount: initialData.discount_amount || '',
                gross_total: initialData.gross_total || '',
                sub_total: initialData.sub_total || '',
                vat_amount: initialData.vat_amount || '',

                is_advance_job: false,
                net_total: initialData.net_total || '',
                advance_payment: initialData.advance_payment || parseFloat(0).toFixed(3),
                balance: ((parseFloat(initialData.net_total) || 0) - (parseFloat(initialData.advance_payment) || 0)).toFixed(3),
                payment: parseFloat(0).toFixed(3),
                payment_type: initialData?.payment_type || '',
                payment_date: initialData?.payment_date || new Date().toISOString().split('T')[0],
                payment_amount: initialData?.payment_amount ? (parseFloat(initialData.payment_amount) || 0).toFixed(3) : '',
                payment_reference: initialData?.payment_reference || '',
                payments: initialData.payments ? initialData.payments.map(payment => ({
                    ...payment,
                    payment_amount: (parseFloat(payment.payment_amount) || 0).toFixed(3)
                })) : [],


                trouser: {
                    id: initialData.trouser?.id || '',
                    waist_measurement: initialData.trouser?.waist_measurement || 0,
                    hip_measurement: initialData.trouser?.hip_measurement || 0,
                    waist_line_to_heel: initialData.trouser?.waist_line_to_heel || 0,
                    thigh_measurement: initialData.trouser?.thigh_measurement || 0,
                    knee_measurement: initialData.trouser?.knee_measurement || 0,
                    bottom_measurement: initialData.trouser?.bottom_measurement || 0,
                    waist_to_crotch: initialData.trouser?.waist_to_crotch || 0,
                    back_condition: initialData.trouser?.back_condition || ''
                },
                jacket: {
                    id: initialData.jacket?.id || '',
                    shoulder_seam_to_first_bone: initialData.jacket?.shoulder_seam_to_first_bone || 0,
                    around_chest: initialData.jacket?.around_chest || 0,
                    around_waist: initialData.jacket?.around_waist || 0,
                    around_hip: initialData.jacket?.around_hip || 0,
                    tip_to_tip_shoulder: initialData.jacket?.tip_to_tip_shoulder || 0,
                    center_back_sleeve_seam: initialData.jacket?.center_back_sleeve_seam || 0,
                    shoulder_tip_to_below_wrist: initialData.jacket?.shoulder_tip_to_below_wrist || 0,
                    color_seam_natural_waist: initialData.jacket?.color_seam_natural_waist || 0,
                    shoulder_seam_mid_rise: initialData.jacket?.shoulder_seam_mid_rise || 0,
                    sleeve_length: initialData.jacket?.sleeve_length || 0,
                    neck_around: initialData.jacket?.neck_around || 0,
        
                    shoulder_type: initialData.jacket?.shoulder_type || '',
                    chest_type: initialData.jacket?.chest_type || ''
                },
            });
        }
    }, [initialData]);

    useEffect(() => {
        if (locations) {
            setFormData(prev => ({
                ...prev,
                location: prev.location || locations?.find(loc => loc.is_primary === 1) || null
            }));
        }
    }, [locations]);

    useEffect(() => {
        if (customer_locations) {
            setFormData(prev => ({
                ...prev,
                customer_location: prev.customer_location || customer_locations?.find(loc => loc.is_primary === 1) || null
            }));
        }
    }, [customer_locations]);

    useEffect(() => {
        if (branches) {
            setFormData(prev => ({
                ...prev,
                branch: prev.branch || (branches && branches.length > 0 ? branches[0] : null)
            }));
        }
    }, [branches]);

    const calculateFormData = (formData) => {
        const grossTotal = calculateGrossTotal(formData.items);

        const overallDiscountAmount = calculateOverallDiscount(
            grossTotal,
            parseFloat(formData.discount_percentage) || 0,
            parseFloat(formData.discount_amount) || 0
        );
        const itemsWithHeaderDiscount = distributeHeaderDiscount(
            formData.items,
            overallDiscountAmount
        );
        const totals = calculateTotals(itemsWithHeaderDiscount);

        return {
            gross_total: grossTotal.toFixed(3),
            discount_amount: overallDiscountAmount.toFixed(3),
            sub_total: (grossTotal - overallDiscountAmount).toFixed(3),
            vat_amount: totals.vatAmount.toFixed(3),
            net_total: totals.netTotal.toFixed(3),
            balance: ((parseFloat(totals.netTotal) || 0) - (parseFloat(formData.advance_payment) || 0)).toFixed(3),
            items: itemsWithHeaderDiscount,

            payment_type: '', 
            payment_amount: '', 
            payment_date: new Date().toISOString().split('T')[0], 
            payment_reference: '',
            payments: []
        };
    }

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const itemFieldMatch = name.match(/^items\[(\d+)\]\.(.+)$/);
        const nestedFieldMatch = name.match(/^(trouser|jacket)\.(.+)$/);

        setFormData(prev => {
            let newData = { ...prev };

            // Validate discount_percentage
            if (name === 'discount_percentage') {
                const numValue = parseFloat(value) || 0;
                if (numValue > 100) {
                    return prev; // Don't update if value exceeds 100
                }
            }

            if (itemFieldMatch) {
                const itemIndex = parseInt(itemFieldMatch[1]);
                const field = itemFieldMatch[2];
                
                const newItems = [...prev.items];
                const updatedItem = {
                    ...newItems[itemIndex],
                    [field]: type === 'checkbox' ? checked : value
                };

                // Only reset calculated values if the field is truly empty (not zero)
                if (['quantity', 'unit_price'].includes(field) && (value === '' || value === 0 || value === '0')) {
                    updatedItem.line_amount = '0.000';
                    updatedItem.vat_amount = '0.000';
                    updatedItem.header_discount = '0.000';
                    updatedItem.amount_excl_vat = '0.000';
                    updatedItem.amount_incl_vat = '0.000';
                } else if (['quantity', 'unit_price', 'discount_percentage'].includes(field)) {
                    const calculatedValues = calculateItemValues(updatedItem);
                    Object.assign(updatedItem, calculatedValues);
                }

                newItems[itemIndex] = updatedItem;
                newData.items = newItems;
            } else if (nestedFieldMatch) {
                // Handle nested fields like trouser.waist_measurement or jacket.something
                const [parent, child] = nestedFieldMatch.slice(1);
                newData[parent] = {
                    ...newData[parent],
                    [child]: type === 'checkbox' ? checked : value
                };
            } else {
                newData[name] = type === 'checkbox' ? checked : value;
            }

            const calculatedData = calculateFormData(newData);
            return {
                ...newData,
                ...calculatedData
            };
        });
    };

    const handleDiscountChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => {
            return {
                ...prev,
                [name]: value
            }
        });

        if (value.length > 0) {
            clearTimeout(typingTimer);
            const timer = setTimeout(() => doneTyping(value, name), doneTypingInterval);
            setTypingTimer(timer);
        } else {
            doneTyping(value, name);
            clearTimeout(typingTimer);
        }
    }

    const doneTyping = async (value, name) => {
        setFormData(prev => {
            let newData = { ...prev };
            newData[name] = value;
            if(prev.gross_total*1 < value*1) {
                newData[name] = prev.gross_total;
            }

            const calculatedData = calculateFormData(newData);
            return {
                ...newData,
                ...calculatedData
            }; 
        });
    };

    

    const handleSelectChange = (name, value) => {
        if(name === 'customer') {
            
            setFormData(prev => {
                const isDomestic = value?.is_domestic !== undefined ? value.is_domestic : prev.is_domestic;

                // Update items with new VAT percentage based on is_domestic
                const updatedItems = prev.items.map(item => {
                    const vatPercentage = isDomestic 
                        ? item.vat_product_group?.vat_percentage || '0.000'
                        : '0.000';
                    
                    // Recalculate item values with new VAT percentage
                    const calculatedValues = calculateItemValues({
                        ...item,
                        vat_percentage: vatPercentage
                    });

                    return {
                        ...item,
                        vat_percentage: vatPercentage,
                        ...calculatedValues
                    };
                });
                
                const newData = {
                    ...prev,
                    [name]: value,
                    customer_name: value?.name || prev.customer_name,
                    phone_number: value?.phone || prev.phone_number,
                    vat_number: value?.vat_number || prev.vat_number,
                    is_domestic: (value?.is_domestic === 0) ? false : true || prev.is_domestic,
                    country: value?.country || prev.country,
                    vat_group: value?.vat_group ? {
                        id: value.vat_group.id,
                        label: value.vat_group.label,
                    } : prev.vat_group,
                    address1: value?.address1,
                    address2: value?.address2,
                    items: updatedItems,
                };

                const calculatedData = calculateFormData(newData);
                return {
                    ...newData,
                    ...calculatedData
                };
            });
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleCustomerFormUpdate = (response, prev) => {
        const isDomestic = response?.is_domestic !== undefined ? response.is_domestic : prev.is_domestic;
        
        // Update items with new VAT percentage based on is_domestic
        const updatedItems = prev.items.map(item => {
            const vatPercentage = isDomestic 
                ? item.vat_product_group?.vat_percentage || '0.000'
                : '0.000';
            
            // Recalculate item values with new VAT percentage
            const calculatedValues = calculateItemValues({
                ...item,
                vat_percentage: vatPercentage
            });

            return {
                ...item,
                vat_percentage: vatPercentage,
                ...calculatedValues
            };
        });

        const newData = {
            ...prev,
            customer: {
                id: response.id,
                label: response.label
            },
            customer_name: response?.name || prev.customer_name,
            phone_number: response?.phone || prev.phone_number,
            vat_number: response?.vat_number || prev.vat_number,
            is_domestic: (response?.is_domestic === 0) ? false : true || prev.is_domestic,
            country: response?.country || prev.country,
            vat_group: response?.vat_group || prev.vat_group,
            address1: response?.address1,
            address2: response?.address2,
            items: updatedItems,
        };

        const calculatedData = calculateFormData(newData);
        return {
            ...newData,
            ...calculatedData
        };
    };

    const handleCustomerAddClick = HandleQuickCreate({
        title: 'Create Customer',
        FormComponent: CustomerForm,
        createAction: createCustomer,
        settingsKey: 'customers',
        formDataKey: 'customer',
        moduleName: 'customer',
        setFormData,
        setSettingsAction: setCustomerSettings,
        sidebarType: 'primary',
        className: 'right-sidebar-80',
        onQuickCreateDone: handleCustomerFormUpdate
    });

    const handlePaymentChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        // Auto-fill payment_amount with balance when payment type is selected
        if (name === 'payment_type' && value) {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                payment_amount: parseFloat(prev.balance).toFixed(3) // Auto-fill with balance
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }

        // Validate payment amount against balance when it changes
        if (name === 'payment_amount') {
            const currentBalance = parseFloat(formData.balance) || 0;
            const paymentAmount = parseFloat(value) || 0;
            if (paymentAmount > currentBalance) {
                setErrors(prev => ({
                    ...prev,
                    payment_amount: `Payment amount cannot exceed remaining balance of ${currentBalance.toFixed(3)}`
                }));
            } else if (errors.payment_amount) {
                setErrors(prev => ({...prev, payment_amount: ''}));
            }
        } else if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };
    
    const handleAddPayment = () => {
        // Validate payment type
        if (!formData.payment_type) {
            setErrors(prev => ({
                ...prev,
                payment_type: 'Please select a payment type'
            }));
            return;
        }

        // Validate payment amount
        if (!formData.payment_amount || parseFloat(formData.payment_amount) <= 0) {
            setErrors(prev => ({
                ...prev,
                payment_amount: 'Payment amount must be greater than 0'
            }));
            return;
        }

        // Validate payment amount against balance
        const currentBalance = parseFloat(formData.balance) || 0;
        const paymentAmount = parseFloat(formData.payment_amount) || 0;

        if (paymentAmount > currentBalance) {
            setErrors(prev => ({
                ...prev,
                payment_amount: `Payment amount cannot exceed remaining balance of ${currentBalance.toFixed(3)}`
            }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            payment_type: '', 
            payment_amount: '', 
            payment_date: new Date().toISOString().split('T')[0], 
            payment_reference: '',
            payments: [...prev.payments, { 
                payment_type: prev.payment_type, 
                payment_amount: parseFloat(prev.payment_amount).toFixed(3), 
                payment_date: prev.payment_date, 
                payment_reference: prev.payment_reference 
            }]
        }));

        // Clear any existing errors after successful addition
        setErrors(prev => ({
            ...prev,
            payment_type: '',
            payment_amount: ''
        }));
    };

    const handleDeletePayment = (index) => {
        setFormData(prev => ({
            ...prev,
            payments: prev.payments.filter((_, i) => i !== index)
        }));
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSaving(true);
            await onSave(formData);
            setErrors({});
            onClose();
        } catch (error) {
            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(key => {
                    newErrors[key] = Array.isArray(error.errors[key]) 
                        ? error.errors[key][0] 
                        : error.errors[key];
                });
                setErrors(newErrors);
            } else {
                setErrors({
                    name: error.message || 'An error occurred while saving'
                });
            }
            return;
        } finally {
            setIsSaving(false);
        }
    };

    const isCreateMode = !formData?.id;
    const hasPermission = isCreateMode ? hasCreatePermission : hasUpdatePermission;

    const isSubmitting = isLoading || isSaving || formData.is_locked || !hasPermission;
    const selectedLang = languages.find(l => l.code === selectedLanguage);


    const customerModal = useModal();
    const customerExternalCondition = {
        rules: {
            condition: 'and',
            children: [
                {
                    type: 'rule',
                    query: {
                        operator: 'equal',
                        rule: 'users.customer_type',
                        type: 'text',
                        value: '2'
                    }
                }
            ]
        }
    };

    const handleCustomerSelected = (customer) => {
        handleSelectChange('customer', customer);
        customerModal.hide();
    };


    const itemModal = useModal();
    const handleItemSelected = (selectedItems) => {

        //Commented out to allow duplicate item selections by adding all selected rows.
        // // Get current items from form data
        // const currentItems = formData.items || [];

        // // Filter out items that already exist in the form
        // const newItems = selectedItems.filter(selectedItem => 
        //     !currentItems.some(existingItem => existingItem.item?.id === selectedItem.id)
        // );

        // Allow duplicate item selections by adding all selected rows.
        // Transform selected items to match the form data structure
        const transformedItems = selectedItems.map(item => ({
                id: crypto.randomUUID(),
                item: {
                    id: item.id,
                    item_code: item.item_code,
                    label: item.name?.en || '',
                },
                item_description: '',
                unit: item.measurement_unit?.label || '',
                quantity : '',
                unit_price: item.unit_price || '0.000',
                discount_percentage : '',
                line_amount : '',
                vat_amount : '',
                header_discount : '',
                amount_excl_vat : '',
                amount_incl_vat : '',
                vat_product_group: {
                    id: item.vat_group?.id,
                    label: item.vat_group?.label,
                    vat_percentage: item.vat_group?.vat_percentage || '0.000',
                }, // Convert object to string
                vat_percentage: (formData.is_domestic) ? item.vat_group?.vat_percentage || '0.000' : '0.000',
            }
        ));

        // Update form data with new items
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, ...transformedItems]
        }));
    };


    const handleRemoveItem = (index) => {
        setFormData(prev => {
            const newItems = [...prev.items];
            newItems.splice(index, 1);
            
            const newData = {
                ...prev,
                items: newItems
            };

            const calculatedData = calculateFormData(newData);
            return {
                ...newData,
                ...calculatedData
            };
        });
    };


    // Calculate total payments and balance
    const calculatePaymentTotals = useCallback(() => {
        const totalPayments = formData.payments.reduce((sum, payment) => {
            return sum + (parseFloat(payment.payment_amount) || 0);
        }, 0);

        const netTotal = parseFloat(formData.net_total) || 0;
        const advancePayment = parseFloat(formData.advance_payment) || 0;
        const balance = netTotal - advancePayment - totalPayments;

        setFormData(prev => ({
            ...prev,
            payment: totalPayments.toFixed(3),
            balance: balance.toFixed(3)
        }));
    }, [formData.payments, formData.net_total, formData.advance_payment]);

    useEffect(() => {
        calculatePaymentTotals();
    }, [calculatePaymentTotals]);


    if (isLoadingSettings || !isSettingsInitialized) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-between mb-2">
                <div className="d-flex justify-content-start">
                    <h5 className="mb-0">Create Job Order</h5>
                </div>
                <div className="d-flex justify-content-end">
                    {formData?.id && (
                        <a href={`${process.env.PUBLIC_URL}/job_orders/view/${formData.id}?preview=true`} target="_blank" className="btn btn-primary me-2" >
                            <i className="fas fa-print"></i> Print Preview
                        </a>
                    )}  
                    {/* <LanguageSwitch onLanguageChange={setSelectedLanguage} /> */}
                </div>
            </div>
            <div className="flex-grow-1">
                <div className="row">
                    <div className="col-md-4">
                        <CommonInput
                            name="order_number"
                            label="Order Number"
                            value={formData.order_number}
                            onChange={handleChange}
                            placeholder="Enter order number"
                            disabled={true}
                            errors={errors}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonDate
                            name="order_date"
                            label="Order Date"
                            value={formData.order_date}
                            onChange={handleChange}
                            placeholder="Enter order date"
                            disabled={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <CommonAsyncSelect
                            name="customer"
                            label="Customer Code"
                            value={formData.customer}
                            onChange={(value) => handleSelectChange('customer', value)}
                            loadOptions={async (inputValue) => {
                                
                                const condition = {
                                    rules: {
                                        condition: 'and',
                                        children: [
                                            {
                                                type: 'rule',
                                                query: {
                                                    operator: 'equal',
                                                    rule: 'users.customer_type',
                                                    type: 'text',
                                                    value: '2'
                                                }
                                            }
                                        ]
                                    }
                                }

                                const response = await customerService.getList({ search: inputValue }, condition);
                                return response.data.map(item => ({
                                    id: item.id,
                                    name: item.name || '',
                                    label: item.name?.en+' ('+item.customer_code+')',
                                    country: item.country,
                                    phone: item.phone,
                                    vat_group: item.vat_group,
                                    vat_number: item.vat_number,
                                    is_domestic: item.is_domestic,
                                    address1: item.address1,
                                    address2: item.address2,
                                }));
                            }}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                            actionButtons={[
                                {
                                    icon: 'fa fa-plus',
                                    label: '',
                                    className: 'btn-primary',
                                    onClick: (value) => handleCustomerAddClick()
                                },
                                {
                                    icon: 'fa fa-bars',
                                    label: '',
                                    className: 'btn-primary',
                                    onClick: (value) => customerModal.show()
                                }
                            ]}
                        />
                    </div>
                    <div className="col-md-4">
                        <TranslatableInput
                            label="Customer Name"
                            name="customer_name"
                            value={formData.customer_name}
                            onChange={handleChange}
                            errors={errors}
                            required={true}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="phone_number"
                            label="Phone Number"
                            value={formData.phone_number}
                            onChange={handleChange}
                            placeholder="Enter phone number"
                            disabled={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <TranslatableInput
                            label="Address 1"
                            name="address1"
                            value={formData.address1}
                            onChange={handleChange}
                            errors={errors}
                            required={false}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="address2"
                            label="Address 2"
                            value={formData.address2}
                            onChange={handleChange}
                            placeholder="Enter address 2"
                            disabled={isSubmitting}
                            errors={errors}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonSelect
                            name="country"
                            label="Country"
                            value={formData.country}
                            onChange={(value) => handleSelectChange('country', value)}
                            options={countries}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                            placeholder="Select country..."
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <CommonDate
                            name="trial_date"
                            label="Trial Date"
                            value={formData.trial_date}
                            onChange={handleChange}
                            placeholder="Enter trial date"
                            disabled={isSubmitting}
                            errors={errors}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonDate
                            name="due_date"
                            label="Due Date"
                            value={formData.due_date}
                            onChange={handleChange}
                            placeholder="Enter due date"
                            disabled={isSubmitting}
                            errors={errors}
                        />
                    </div>
                    <div className="col-md-4">
                        <TranslatableTextarea
                            label="Detailed Style Information"
                            name="order_detail_info"
                            value={formData.order_detail_info}
                            onChange={handleChange}
                            errors={errors}
                            required={false}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                            rows={1}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <CommonInput
                            name="vat_number"
                            label="VAT Number"
                            value={formData.vat_number}
                            onChange={handleChange}
                            placeholder="Enter vat number"
                            disabled={isSubmitting}
                            errors={errors}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonSelect
                            name="vat_group"
                            label="VAT Group"
                            value={formData.vat_group}
                            onChange={(value) => handleSelectChange('vat_group', value)}
                            options={[]}
                            isSubmitting={true}
                            errors={errors}
                            required={true}
                            placeholder="Select vat group..."
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonSelect
                            name="customer_location"
                            label="Cust. Location"
                            value={formData.customer_location}
                            onChange={(value) => handleSelectChange('customer_location', value)}
                            options={customer_locations}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                            placeholder="Select customer location..."
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <CommonSelect
                            name="branch"
                            label="Branch"
                            value={formData.branch}
                            onChange={(value) => handleSelectChange('branch', value)}
                            options={branches ? branches : []}
                            isSubmitting={formData?.id ? true : isSubmitting}
                            errors={errors}
                            required={true}
                            placeholder="Select branch..."
                        />
                    </div>
                    <div className="col-md-4">  
                        <CommonSelect
                            name="location"
                            label="Location"
                            value={formData.location}
                            onChange={(value) => handleSelectChange('location', value)}
                            options={locations}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                            placeholder="Select location..."
                        />
                    </div>
                </div>

                <div className="row">
                    <Tab.Container activeKey={activeTab} onSelect={(key) => setActiveTab(key)}>
                        <Nav variant="tabs" className="mb-3">
                            <Nav.Item className="custom-li-trouser mb-0 p-0">
                                <Nav.Link eventKey="trouser">Trouser</Nav.Link>
                            </Nav.Item>
                            <Nav.Item className="custom-li-shirt mb-0 p-0">
                                <Nav.Link eventKey="jacket">Shirt / Jacket</Nav.Link>
                            </Nav.Item>
                            <Nav.Item className="custom-li-item mb-0 p-0">
                                <Nav.Link eventKey="items">Bill Items</Nav.Link>
                            </Nav.Item>
                        </Nav>
                        <Tab.Content>
                            <Tab.Pane eventKey="trouser" className="p-0">
                                <div className="row">
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/trouser/Around Waist.png`} alt="Around Waist" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Around Waist</label>
                                            <CommonGroupInput
                                                name={`trouser.waist_measurement`}
                                                inputClassName="rounded-start-pill px-3"
                                                groupLabel="inch"
                                                minValue={0}
                                                value={formData?.trouser?.waist_measurement}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                                errors={errors}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/trouser/Around Hip.png`} alt="Around Hip" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Around Hip</label>
                                            <CommonGroupInput
                                                name={`trouser.hip_measurement`}
                                                inputClassName="rounded-start-pill px-3"
                                                groupLabel="inch"
                                                minValue={0}
                                                value={formData?.trouser?.hip_measurement}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                                errors={errors}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/trouser/Waist Line To Heel Base Of Shoe-Length.png`} alt="Waist Line To Heel Base Of Shoe-Length" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Waist Line To Heel Base Of Shoe-Length</label>
                                            <CommonGroupInput
                                                name={`trouser.waist_line_to_heel`}
                                                inputClassName="rounded-start-pill px-3"
                                                groupLabel="inch"
                                                minValue={0}
                                                value={formData?.trouser?.waist_line_to_heel}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                                errors={errors}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/trouser/Aound Thigh Or Both Crease Of Trouser-Thigh.png`} alt="Around Thigh Or Both Crease Of Trouser-Thigh" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Around Thigh Or Both Crease Of Trouser-Thigh</label>
                                            <CommonGroupInput
                                                name={`trouser.thigh_measurement`}
                                                inputClassName="rounded-start-pill px-3"
                                                groupLabel="inch"
                                                minValue={0}
                                                value={formData?.trouser?.thigh_measurement}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                                errors={errors}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/trouser/Around Knees Or Both Crease Of Trouser Knee.png`} alt="Around Knees Or Both Crease Of Trouser Knee" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Around Knees Or Both Crease Of Trouser Knee</label>
                                            <CommonGroupInput
                                                name={`trouser.knee_measurement`}
                                                inputClassName="rounded-start-pill px-3"
                                                groupLabel="inch"
                                                minValue={0}
                                                value={formData?.trouser?.knee_measurement}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                                errors={errors}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/trouser/Around Bottom Or Both Crease Of Trouser.png`} alt="Around Bottom Or Both Crease Of Trouser" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Around Bottom Or Both Crease Of Trouser</label>
                                            <CommonGroupInput
                                                name={`trouser.bottom_measurement`}
                                                inputClassName="rounded-start-pill px-3"
                                                groupLabel="inch"
                                                minValue={0}
                                                value={formData?.trouser?.bottom_measurement}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                                errors={errors}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/trouser/Waist Line To Crotch Point Rise Length.png`} alt="Waist Line To Crotch Point Rise Length" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Waist Line To Crotch Point Rise Length</label>
                                            <CommonGroupInput
                                                name={`trouser.waist_to_crotch`}
                                                inputClassName="rounded-start-pill px-3"
                                                groupLabel="inch"
                                                minValue={0}
                                                value={formData?.trouser?.waist_to_crotch}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                                errors={errors}
                                            />
                                        </div>
                                    </div>


                                    <div className='col col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4'>
                                        <CommonImageRadio
                                            name="trouser.back_condition"
                                            id="trouser_back_condition1"
                                            className="image-tick"
                                            value="narrow_back"
                                            label="Erract Narrow Back Head Forward Flat Chest"
                                            image={`${process.env.PUBLIC_URL}/images/trouser/Erract Narrow Back Head Forward Flat Chest.png`}
                                            onChange={handleChange}
                                            checked={formData?.trouser?.back_condition === 'narrow_back'}
                                            disabled={isSubmitting}
                                            errors={errors}
                                        />
                                    </div>
                                    <div className='col col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4'>
                                        <CommonImageRadio
                                            name="trouser.back_condition"
                                            id="trouser_back_condition2"
                                            className="image-tick"
                                            value="round_back"
                                            label="Round Back Head Forward Flat Chest"
                                            image={`${process.env.PUBLIC_URL}/images/trouser/Round Back Head Forward Flat Chest.png`}
                                            onChange={handleChange}
                                            checked={formData?.trouser?.back_condition === 'round_back'}
                                            disabled={isSubmitting}
                                            errors={errors}
                                        />
                                    </div>
                                    <div className='col col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4'>
                                        <CommonImageRadio
                                            name="trouser.back_condition"
                                            id="trouser_back_condition3"
                                            className="image-tick"
                                            value="full_chest"
                                            label="Full Chest and Stomach Hollow Back"
                                            image={`${process.env.PUBLIC_URL}/images/trouser/Full Chest and Stomach Hollow Back.png`}
                                            onChange={handleChange}
                                            checked={formData?.trouser?.back_condition === 'full_chest'}
                                            disabled={isSubmitting}
                                            errors={errors}
                                        />
                                    </div>
                                </div>
                            </Tab.Pane>
                            <Tab.Pane eventKey="jacket" className="p-0">
                                <div className="row justify-content-center">
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/jacket/Shoulder Seam To First Bone.png`} alt="Shoulder Seam To First Bone" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Shoulder Seam To First Bone</label>
                                            <CommonGroupInput
                                                name={`jacket.shoulder_seam_to_first_bone`}
                                                inputClassName="rounded-start-pill px-3"
                                                groupLabel="inch"
                                                minValue={0}
                                                value={formData?.jacket?.shoulder_seam_to_first_bone}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                                errors={errors}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/jacket/Around Chest.png`} alt="Around Chest" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Around Chest</label>
                                            <CommonGroupInput
                                                name={`jacket.around_chest`}
                                                inputClassName="rounded-start-pill px-3"
                                                groupLabel="inch"
                                                minValue={0}
                                                value={formData?.jacket?.around_chest}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                                errors={errors}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/jacket/Around Waist 2.png`} alt="Around Waist" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Around Waist</label>
                                            <CommonGroupInput
                                                name={`jacket.around_waist`}
                                                inputClassName="rounded-start-pill px-3"
                                                groupLabel="inch"
                                                minValue={0}
                                                value={formData?.jacket?.around_waist}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                                errors={errors}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/jacket/Aound Hip 2.png`} alt="Around Hip" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Around Hip</label>
                                            <CommonGroupInput
                                                name={`jacket.around_hip`}
                                                inputClassName="rounded-start-pill px-3"
                                                groupLabel="inch"
                                                minValue={0}
                                                value={formData?.jacket?.around_hip}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                                errors={errors}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/jacket/Tip To Tip Of Shoulder.png`} alt="Tip To Tip Of Shoulder" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Tip To Tip Of Shoulder</label>
                                            <CommonGroupInput
                                                name={`jacket.tip_to_tip_shoulder`}
                                                inputClassName="rounded-start-pill px-3"
                                                groupLabel="inch"
                                                minValue={0}
                                                value={formData?.jacket?.tip_to_tip_shoulder}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                                errors={errors}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/jacket/Center Of Back To Sleeve Seam.png`} alt="Center Of Back To Sleeve Seam" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Center Of Back To Sleeve Seam</label>
                                            <CommonGroupInput
                                                name={`jacket.center_back_sleeve_seam`}
                                                inputClassName="rounded-start-pill px-3"
                                                groupLabel="inch"
                                                minValue={0}
                                                value={formData?.jacket?.center_back_sleeve_seam}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                                errors={errors}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/jacket/Shoulder Tip To Below Wrist Bone.png`} alt="Shoulder Tip To Below Wrist Bone" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Shoulder Tip To Below Wrist Bone</label>
                                            <CommonGroupInput
                                                name={`jacket.shoulder_tip_to_below_wrist`}
                                                inputClassName="rounded-start-pill px-3"
                                                groupLabel="inch"
                                                minValue={0}
                                                value={formData?.jacket?.shoulder_tip_to_below_wrist}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                                errors={errors}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/jacket/Color Seam To Natural Waist.png`} alt="Color Seam To Natural Waist" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Color Seam To Natural Waist</label>
                                            <CommonGroupInput
                                                name={`jacket.color_seam_natural_waist`}
                                                inputClassName="rounded-start-pill px-3"
                                                groupLabel="inch"
                                                minValue={0}
                                                value={formData?.jacket?.color_seam_natural_waist}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                                errors={errors}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/jacket/Shoulder Seam To Mid Rise.png`} alt="Shoulder Seam To Mid Rise" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Shoulder Seam To Mid Rise</label>
                                            <CommonGroupInput
                                                name={`jacket.shoulder_seam_mid_rise`}
                                                inputClassName="rounded-start-pill px-3"
                                                groupLabel="inch"
                                                minValue={0}
                                                value={formData?.jacket?.shoulder_seam_mid_rise}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                                errors={errors}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/jacket/Sleeve Length Tip Of Shoulder To2.png`} alt="Sleeve Length Tip Of Shoulder To" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Sleeve Length Tip Of Shoulder To</label>
                                            <CommonGroupInput
                                                name={`jacket.sleeve_length`}
                                                inputClassName="rounded-start-pill px-3"
                                                groupLabel="inch"
                                                minValue={0}
                                                value={formData?.jacket?.sleeve_length}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                                errors={errors}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-2 mb-4">
                                        <div className="d-flex flex-column align-items-center text-center p-3 border rounded shadow-sm h-100">
                                            <img src={`${process.env.PUBLIC_URL}/images/jacket/Neck Around Neck2.png`} alt="Neck Around Neck" className="img-fluid"/>
                                            <label className="fs-12-text mb-2 text-dark">Neck Around Neck</label>
                                            <CommonGroupInput
                                                name={`jacket.neck_around`}
                                                inputClassName="rounded-start-pill px-3"
                                                groupLabel="inch"
                                                minValue={0}
                                                value={formData?.jacket?.neck_around}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                                errors={errors}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className='row'>
                                    <div className='col-xl-12 col-xxl-6'>
                                        <div className='row justify-content-center'>

                                            <div className="col-12 text-center mb-4">
                                                <h4 className="fs-6 position-relative d-inline-block pb-2" style={{borderBottom: "2px solid #405189",letterSpacing: "1px"}}>
                                                    Shoulder Type
                                                </h4>
                                            </div>

                                            <div className='col col-sm-4 col-md-3 col-xxl-4 mb-4'>
                                                <CommonImageRadio
                                                    name="jacket.shoulder_type"
                                                    id="jacket_shoulder_type1"
                                                    className="image-tick"
                                                    value="long_neck"
                                                    label="Sloping Should Long Neck"
                                                    image={`${process.env.PUBLIC_URL}/images/jacket/Sloping Should Long Neck.png`}
                                                    onChange={handleChange}
                                                    checked={formData?.jacket?.shoulder_type === 'long_neck'}
                                                    disabled={isSubmitting}
                                                    errors={errors}
                                                />
                                            </div>
                                            <div className='col col-sm-4 col-md-3 col-xxl-4 mb-4'>
                                                <CommonImageRadio
                                                    name="jacket.shoulder_type"
                                                    id="jacket_shoulder_type2"
                                                    className="image-tick"
                                                    value="regular_shoulder"
                                                    label="Sloping Regular Shoulder"
                                                    image={`${process.env.PUBLIC_URL}/images/jacket/Sloping Regular Shoulder.png`}
                                                    onChange={handleChange}
                                                    checked={formData?.jacket?.shoulder_type === 'regular_shoulder'}
                                                    disabled={isSubmitting}
                                                    errors={errors}
                                                />
                                            </div>
                                            <div className='col col-sm-4 col-md-3 col-xxl-4 mb-4'>
                                                <CommonImageRadio
                                                    name="jacket.shoulder_type"
                                                    id="jacket_shoulder_type3"
                                                    className="image-tick"
                                                    value="square_shoulder"
                                                    label="Square Shoulder" 
                                                    image={`${process.env.PUBLIC_URL}/images/jacket/Square Shoulder Short Neck.png`}
                                                    onChange={handleChange}
                                                    checked={formData?.jacket?.shoulder_type === 'square_shoulder'}
                                                    disabled={isSubmitting}
                                                    errors={errors}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className='col-xl-12 col-xxl-6'>
                                        <div className='row justify-content-center'>
                                            <div className="col-12 text-center mb-4">
                                                <h4 className="fs-6 position-relative d-inline-block pb-2" style={{borderBottom: "2px solid #405189",letterSpacing: "1px"}}>
                                                    Chest Type
                                                </h4>
                                            </div>

                                            <div className='col col-sm-4 col-md-3 col-xxl-4 mb-4'>
                                                <CommonImageRadio
                                                    name="jacket.chest_type"
                                                    id="jacket_chest_type1"
                                                    className="image-tick"
                                                    value="narrow_back_proment"
                                                    label="Erract Narrow Back Proment Chest"
                                                    image={`${process.env.PUBLIC_URL}/images/jacket/Erract Narrow Back Proment Chest2.png`}
                                                    onChange={handleChange}
                                                    checked={formData?.jacket?.chest_type === 'narrow_back_proment'}
                                                    disabled={isSubmitting}
                                                    errors={errors}
                                                />
                                            </div>
                                            <div className='col col-sm-4 col-md-3 col-xxl-4 mb-4'>
                                                <CommonImageRadio
                                                    name="jacket.chest_type"
                                                    id="jacket_chest_type2"
                                                    className="image-tick"
                                                    value="forward_flat_chest"
                                                    label="Round Back Head Forward Flat Chest"  
                                                    image={`${process.env.PUBLIC_URL}/images/jacket/Round Back Head Forward Flat Chest2.png`}
                                                    onChange={handleChange}
                                                    checked={formData?.jacket?.chest_type === 'forward_flat_chest'}
                                                    disabled={isSubmitting}
                                                    errors={errors}
                                                />
                                            </div>
                                            <div className='col col-sm-4 col-md-3 col-xxl-4 mb-4'>
                                                <CommonImageRadio
                                                    name="jacket.chest_type"
                                                    id="jacket_chest_type3"
                                                    className="image-tick"
                                                    value="stomach_hollow_back"
                                                    label="Full Chest And Stomach Hollow Back"
                                                    image={`${process.env.PUBLIC_URL}/images/jacket/Full Chest And Stomach Hollow Back2.png`}
                                                    onChange={handleChange}
                                                    checked={formData?.jacket?.chest_type === 'stomach_hollow_back'}
                                                    disabled={isSubmitting}
                                                    errors={errors}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    
                                </div>
                            </Tab.Pane>
                            <Tab.Pane eventKey="items" className="p-0">
                                {hasCreatePermission || hasUpdatePermission ? (
                                <button type="button" className="btn btn-primary" onClick={() => itemModal.show()}>
                                    Add Item
                                </button>
                                ) : null}

                                <div className="table-responsive mt-4">
                                    <table className="table table-bordered">
                                        <thead>
                                            <tr>
                                                <th className="text-nowrap p-3">Item Code</th>
                                                <th className="text-nowrap p-3">Item Name</th>
                                                <th className="text-nowrap p-3">Unit</th>
                                                <th className="text-nowrap p-3" style={{ minWidth: '150px' }}>Qty</th>
                                                <th className="text-nowrap p-3">Unit Price</th>
                                                <th className="text-nowrap p-3">Discount %</th>
                                                <th className="text-nowrap p-3">Line Amount</th>
                                                <th className="text-nowrap p-3">Header Discount</th>
                                                <th className="text-nowrap p-3">Amt Excl VAT</th>
                                                <th className="text-nowrap p-3">VAT Amount</th>
                                                <th className="text-nowrap p-3">Amt Incl VAT</th>
                                                <th className="text-nowrap p-3">Spl. Item Description</th>
                                                <th className="text-nowrap p-3">VAT Product Group</th>
                                                <th className="text-nowrap p-3" style={{ minWidth: '100px' }}>VAT %</th>
                                                <th className="text-nowrap p-3">Remove</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formData.items && formData.items.length > 0 ? (
                                                formData.items.map((item, index) => (
                                                    <tr key={index}>
                                                        <td className="text-nowrap p-3 align-middle">{item.item?.item_code || 'N/A'}</td>
                                                        <td className="text-nowrap p-3 align-middle">{item.item?.label || 'N/A'}</td>
                                                        <td className="p-3 align-middle">{item.unit || 'N/A'}</td>
                                                        <td className="p-3 align-middle">
                                                            <ItemInput
                                                                index={index}
                                                                field="quantity"
                                                                value={item.quantity}
                                                                onChange={handleChange}
                                                                disabled={isSubmitting}
                                                                errors={errors}
                                                                type="number"
                                                                required={true}
                                                                displayError={false}
                                                            />
                                                        </td>
                                                        <td className="p-3 align-middle">
                                                            <ItemInput
                                                                index={index}
                                                                field="unit_price"
                                                                value={item.unit_price}
                                                                onChange={handleChange}
                                                                disabled={isSubmitting}
                                                                errors={errors}
                                                                type="number"
                                                                required={true}
                                                                displayError={false}
                                                            />
                                                        </td>
                                                        <td className="p-3 align-middle">
                                                            <ItemInput
                                                                index={index}
                                                                field="discount_percentage"
                                                                value={item.discount_percentage}
                                                                onChange={handleChange}
                                                                disabled={isSubmitting}
                                                                errors={errors}
                                                                type="number"
                                                                displayError={false}
                                                            />
                                                        </td>
                                                        <td className="p-3 align-middle">{item.line_amount || '0.00'}</td>
                                                        <td className="p-3 align-middle">{item.header_discount || '0.00'}</td>
                                                        <td className="p-3 align-middle">{item.amount_excl_vat || '0.00'}</td>
                                                        <td className="p-3 align-middle">{item.vat_amount || '0.00'}</td>
                                                        <td className="p-3 align-middle">{item.amount_incl_vat || '0.00'}</td>
                                                        <td className="p-3 align-middle">
                                                            <ItemInput
                                                                index={index}
                                                                field="item_description"
                                                                value={item.item_description}
                                                                onChange={handleChange}
                                                                disabled={isSubmitting}
                                                                errors={errors}
                                                                type="textarea"
                                                                rows={1}
                                                                required={true}
                                                                displayError={false}
                                                            />
                                                        </td>
                                                        <td className="p-3 align-middle">{item.vat_product_group?.label || 'N/A'}</td>
                                                        <td className="p-3 align-middle">
                                                            <ItemInput
                                                                index={index}
                                                                field="vat_percentage"
                                                                value={item.vat_percentage}
                                                                onChange={handleChange}
                                                                disabled={isSubmitting}
                                                                errors={errors}
                                                                type="number"
                                                                required={true}
                                                                displayError={false}
                                                            />
                                                        </td>
                                                        <td className="p-3 align-middle">
                                                            <div>
                                                                <button 
                                                                    type="button"
                                                                    className="btn btn-danger btn-sm"
                                                                    onClick={() => handleRemoveItem(index)}
                                                                    disabled={isSubmitting}
                                                                >
                                                                    <i className="fas fa-trash text-white"></i>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="19" className="text-center">No items added</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>


                                <div className="row mt-4 d-flex justify-content-end">

                                    <div className="col-md-4 order-md-2">
                                        <div className="mb-2 row">
                                            <label className="col-lg-6 col-md-6 col-form-label">Gross Total:</label>
                                            <div className="col-lg-6 col-md-6">
                                                <CommonInput
                                                    type="number"
                                                    min={0}
                                                    className="mb-0 custom-style text-end"
                                                    name="gross_total"
                                                    value={formData.gross_total}
                                                    onChange={handleChange}
                                                    disabled={true}
                                                    errors={errors}
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-2 row">
                                            <label className="col-lg-6 col-md-6 col-form-label">Discount %:</label>
                                            <div className="col-lg-6 col-md-6">
                                                <CommonInput
                                                    type="number"
                                                    className="mb-0 custom-style text-end"
                                                    name="discount_percentage"
                                                    value={formData.discount_percentage}
                                                    onChange={handleChange}
                                                    disabled={isSubmitting}
                                                    errors={errors}
                                                    min={0}
                                                    max={100}
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-2 row">
                                            <label className="col-lg-6 col-md-6 col-form-label">Discount Amt:</label>
                                            <div className="col-lg-6 col-md-6">
                                                <CommonInput
                                                    type="number"
                                                    min={0}
                                                    className="mb-0 custom-style text-end"
                                                    name="discount_amount"
                                                    value={formData.discount_amount}
                                                    onChange={handleDiscountChange}
                                                    disabled={isSubmitting}
                                                    errors={errors}
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-2 row">
                                            <label className="col-lg-6 col-md-6 col-form-label">Sub Total:</label>
                                            <div className="col-lg-6 col-md-6">
                                                <CommonInput
                                                    type="number"
                                                    min={0}
                                                    className="mb-0 custom-style text-end"
                                                    name="sub_total"
                                                    value={formData.sub_total}
                                                    onChange={handleChange}
                                                    disabled={true}
                                                    errors={errors}
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-2 row">
                                            <label className="col-lg-6 col-md-6 col-form-label">VAT Amt:</label>
                                            <div className="col-lg-6 col-md-6">
                                                <CommonInput
                                                    type="number"
                                                    min={0}
                                                    className="mb-0 custom-style text-end"
                                                    name="vat_amount"
                                                    value={formData.vat_amount}
                                                    onChange={handleChange}
                                                    disabled={true}
                                                    errors={errors}
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-2 row">
                                            <label className="col-lg-6 col-md-6 col-form-label">Net Total:</label>
                                            <div className="col-lg-6 col-md-6">
                                                <CommonInput
                                                    type="number"
                                                    min={0}
                                                    className="mb-0 custom-style text-end"
                                                    name="net_total"
                                                    value={formData.net_total}
                                                    onChange={handleChange}
                                                    disabled={true}
                                                    errors={errors}
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-2 row">
                                            <label className="col-lg-6 col-md-6 col-form-label">Advance Payment:</label>
                                            <div className="col-lg-6 col-md-6">
                                                <CommonInput
                                                    type="number"
                                                    min={0}
                                                    className="mb-0 custom-style text-end"
                                                    name="advance_payment"
                                                    value={formData.advance_payment}
                                                    onChange={handleChange}
                                                    disabled={true}
                                                    errors={errors}
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-2 row">
                                            <label className="col-lg-6 col-md-6 col-form-label">Balance:</label>
                                            <div className="col-lg-6 col-md-6">
                                                <CommonInput
                                                    type="number"
                                                    min={0}
                                                    className="mb-0 custom-style text-end"
                                                    name="balance"
                                                    value={formData.balance}
                                                    onChange={handleChange}
                                                    disabled={true}
                                                    errors={errors}
                                                />
                                            </div>
                                        </div>

                                        { !formData.id && (
                                        <div className="mb-2 row">
                                            <label className="col-lg-6 col-md-6 col-form-label">Is Advance Job?</label>
                                            <div className="col-lg-6 col-md-6">
                                                <CommonCheckbox
                                                    name="is_advance_job"
                                                    label=""
                                                    value={formData.is_advance_job}
                                                    onChange={handleChange}
                                                    errors={errors}
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                        </div>
                                        )}
                                    </div>


                                    { !formData.id && formData.is_advance_job && (
                                        <div className="col-12 col-md-6 order-md-1">
                                            <div className="mb-2 row">
                                                <label className="col-lg-6 col-md-6 col-form-label">Payment:</label>
                                                <div className="col-lg-6 col-md-6">
                                                    <CommonInput
                                                        type="number"
                                                        min={0}
                                                        className="mb-0 custom-style text-end"
                                                        name="payment"
                                                        value={formData.payment}
                                                        onChange={handleChange}
                                                        disabled={true}
                                                        errors={errors}
                                                    />
                                                </div>
                                            </div>

                                            <div className="row">
                                                <div className="col-md-12">
                                                    <div className="table-responsive mt-4 mb-2">
                                                        <table className="table table-bordered">
                                                            <thead>
                                                                <tr>
                                                                    <th>Type</th>
                                                                    <th>Amount</th>
                                                                    <th>Date</th>
                                                                    <th>Reference</th>
                                                                    <th>Action</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {formData?.payments && formData.payments.length > 0 ? (
                                                                    formData.payments.map((payment, index) => (
                                                                        <tr key={index}>
                                                                            <td>{payment.payment_type}</td>
                                                                            <td>{payment.payment_amount}</td>
                                                                            <td>{payment.payment_date}</td>
                                                                            <td>{payment.payment_reference}</td>
                                                                            <td>
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-danger btn-sm"
                                                                                    onClick={() => handleDeletePayment(index)}
                                                                                    disabled={isSubmitting}
                                                                                >
                                                                                    <i className="fa fa-trash"></i>
                                                                                </button>
                                                                            </td>
                                                                        </tr>
                                                                    ))
                                                                ) : (
                                                                    <tr>
                                                                        <td colSpan="5" className="text-center">No payments added</td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                        {errors?.payments && (
                                                            <div className="invalid-feedback">
                                                                {Array.isArray(errors['payments']) ? errors['payments'][0] : errors['payments']}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className='row'>
                                                <div className='col-md-12'>
                                                    <div className='row justify-content-center'>
                                                        <div className='col col-sm-4 col-md-4 col-xxl-4 mb-4'>
                                                            <CommonIconRadio
                                                                name="payment_type"
                                                                id="payment_type1"
                                                                value="cash"
                                                                label="Cash"
                                                                leftIcon="fa fa-money-bill"
                                                                onChange={handlePaymentChange}
                                                                checked={formData?.payment_type === 'cash'}
                                                                disabled={isSubmitting}
                                                                errors={errors}
                                                                tickContainerClassName="radio_tick_container"
                                                            />
                                                        </div>
                                                        <div className='col col-sm-4 col-md-4 col-xxl-4 mb-4'>
                                                            <CommonIconRadio
                                                                name="payment_type"
                                                                id="payment_type2"
                                                                value="bank"
                                                                label="Bank"  
                                                                leftIcon="fa fa-bank"
                                                                onChange={handlePaymentChange}
                                                                checked={formData?.payment_type === 'bank'}
                                                                disabled={isSubmitting}
                                                                errors={errors}
                                                                tickContainerClassName="radio_tick_container"
                                                            />
                                                        </div>
                                                        <div className='col col-sm-4 col-md-4 col-xxl-4 mb-4'>
                                                            <CommonIconRadio
                                                                name="payment_type"
                                                                id="payment_type3"
                                                                value="card"
                                                                label="Card"
                                                                leftIcon="fa fa-credit-card"
                                                                onChange={handlePaymentChange}
                                                                checked={formData?.payment_type === 'card'}
                                                                disabled={isSubmitting}
                                                                errors={errors}
                                                                tickContainerClassName="radio_tick_container"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label className="form-label">Payment Amount</label>
                                                        <CommonInput
                                                            type="number"
                                                            name="payment_amount"
                                                            onChange={handlePaymentChange}
                                                            disabled={isSubmitting}
                                                            value={formData.payment_amount}
                                                            errors={errors}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label className="form-label">Payment Date</label>
                                                        <CommonDate
                                                            type="date"
                                                            name="payment_date"
                                                            onChange={handlePaymentChange}
                                                            disabled={isSubmitting}
                                                            value={formData.payment_date}
                                                            errors={errors}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-12">
                                                    <div className="mb-3">
                                                        <label className="form-label">Payment Reference</label>
                                                        <CommonTextarea
                                                            type="text"
                                                            rows={2}
                                                            name="payment_reference"
                                                            onChange={handlePaymentChange}
                                                            disabled={isSubmitting}
                                                            value={formData.payment_reference}
                                                            errors={errors}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-12">
                                                    <button type="button" className="btn btn-warning" onClick={handleAddPayment}>Add Payment</button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                </div>


                <DataSelectionModal
                    show={customerModal.isVisible}
                    onHide={customerModal.hide}
                    title=""
                    DataComponent={Customers}
                    selectionMode="single"
                    onDataSelected={handleCustomerSelected}
                    componentProps={{
                        excludedActions: ['create', 'actions', 'filter'],
                        externalCondition: customerExternalCondition,
                        columnKeys: [],
                    }}
                />

                <DataSelectionModal
                    closeOnAdd={false}
                    show={itemModal.isVisible}
                    onHide={itemModal.hide}
                    title=""
                    DataComponent={FinishedItems}
                    selectionMode="multiple"
                    onRowsSelected={handleItemSelected}
                    selectionButtonText="Add Items"
                    selectionButtonIcon="fa fa-shopping-cart"
                    componentProps={{
                        excludedActions: ['create', 'actions', 'filter'],
                        columnKeys: ['name', 'item_code', 'category', 'unit_price', 'status'],
                    }}
                />
            </div>
            
            {hasPermission && (
            <div className="form-footer">
                <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isSubmitting}
                >
                    {isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Loading...
                        </>
                    ) : 
                    formData.is_locked ? (
                        <>
                            <i className="fa fa-lock me-2" /> Locked
                        </>
                    ) :
                    isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                        </>
                    ) : 'Save'}
                </button>
            </div>
            )}
        </form>
    );
};

export default JobOrderForm; 